// Landing page for every out-of-band auth hand-off:
//   * Google OAuth  -> #access_token=...&refresh_token=...&expires_at=...
//   * magic link    -> ?token=...&kind=magic_link
//   * verify e-mail -> ?token=...&kind=verify_email
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { adoptSession, consumeAuthLink } from "@/lib/auth";

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    // Tokens are single-use; StrictMode's double effect must not consume them twice.
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        const search = new URLSearchParams(window.location.search);
        const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));

        const accessToken = hash.get("access_token");
        const refreshToken = hash.get("refresh_token");

        if (accessToken && refreshToken) {
          await adoptSession({
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_at: Number(hash.get("expires_at") ?? 0),
          });
        } else {
          const token = search.get("token");
          const kind = search.get("kind");
          if (!token || (kind !== "magic_link" && kind !== "verify_email")) {
            throw new Error("Bağlantı eksik veya hatalı");
          }
          await consumeAuthLink(token, kind);
        }

        // Drop the credentials from the address bar before handing over to the app.
        window.history.replaceState(null, "", window.location.pathname);
        await navigate({ to: "/", replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Giriş tamamlanamadı");
      }
    })();
  }, [navigate]);

  return (
    <div
      className="min-h-screen grid place-items-center p-6 text-white text-center"
      style={{
        background: "linear-gradient(135deg,#0a0a1a 0%,#141432 45%,#1e1e5a 80%,#4f46e5 100%)",
      }}
    >
      {error ? (
        <div className="nova-glass max-w-sm rounded-2xl p-6">
          <div className="font-display text-lg font-bold">Giriş tamamlanamadı</div>
          <p className="mt-2 text-sm text-white/70">{error}</p>
          <a
            href="/"
            className="mt-5 inline-flex rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold hover:bg-white/25"
          >
            Giriş ekranına dön
          </a>
        </div>
      ) : (
        <div className="animate-pulse">Giriş yapılıyor…</div>
      )}
    </div>
  );
}
